import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import Header from './Header';
import Footer from './Footer';
import { Hide } from 'roo-ui/components';
import FocusManager from 'components/FocusManager';
import { isIOSDevice, fixBodyScroll, unfixBodyScroll, isPhone } from 'lib/browser';
import { ANIMATION_DURATION } from './constants';
import { useTransition } from 'hooks/useTransition';
import { ResponsiveContainer, Body, Inner } from './primitives';

const ESC = 27;

const ResponsiveModal = React.memo(({ title, children, onOpenModal, onSubmit, onCancel, onBlur, submitButtonText }) => {
  const scrollRef = useRef();
  const [transitionToOpen, setTransitionToOpen] = useState(false);
  const transitionState = useTransition({ transition: transitionToOpen, duration: ANIMATION_DURATION });

  // NOTE: we derive isOpen here, so children do not render one frame before the container
  const isOpen = transitionState !== 'exited';

  const displayFooter = onSubmit !== noop || submitButtonText;

  useEffect(() => {
    if (transitionState === 'entering') {
      if (isFunction(onOpenModal)) onOpenModal();
      if (isPhone()) fixBodyScroll();
      // scroll to force the modal to lock into it's fixed position if a field is focused after the keyboard is opened
      // 100px ensures that we get past the address bar if it wasn't active and gets forced into view
      if (isPhone() && isIOSDevice()) window.scrollBy(0, -100);
    }

    if (transitionState === 'exited') {
      if (isPhone()) unfixBodyScroll();
    }
  }, [onOpenModal, transitionState]);

  useEffect(() => {
    return () => unfixBodyScroll();
  }, []);

  const openModal = useCallback(() => {
    setTransitionToOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setTransitionToOpen(false);
  }, []);

  const blurModal = useCallback(() => {
    if (!transitionToOpen) return;
    closeModal();
    onBlur();
  }, [closeModal, onBlur, transitionToOpen]);

  const submitModal = useCallback(() => {
    closeModal();
    onSubmit();
  }, [closeModal, onSubmit]);

  const cancelModal = useCallback(() => {
    closeModal();
    onCancel();
  }, [closeModal, onCancel]);

  useEffect(() => {
    const handleKeyUp = (event) => {
      if (event.keyCode !== ESC) return;
      if (document.activeElement) document.activeElement.blur();
      cancelModal();
    };
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [cancelModal]);

  return (
    <ResponsiveContainer transitionState={transitionState}>
      <FocusManager onBlur={blurModal}>
        <Hide sm md lg>
          <Header title={title} transitionState={transitionState} cancelModal={cancelModal} />
        </Hide>
        <Body transitionState={transitionState} ref={scrollRef}>
          <Inner transitionState={transitionState} data-testid="modal-content" data-transition-state={transitionState}>
            {children({ isOpen, openModal, submitModal, blurModal, cancelModal })}
          </Inner>
        </Body>
        {displayFooter && (
          <Hide sm md lg>
            <Footer submitModal={submitModal} transitionState={transitionState} buttonText={submitButtonText} />
          </Hide>
        )}
      </FocusManager>
    </ResponsiveContainer>
  );
});

ResponsiveModal.displayName = 'ResponsiveModal';

ResponsiveModal.propTypes = {
  title: PropTypes.string,
  children: PropTypes.func.isRequired,
  onOpenModal: PropTypes.func,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onBlur: PropTypes.func,
  submitButtonText: PropTypes.string,
};

ResponsiveModal.defaultProps = {
  title: '',
  onOpenModal: noop,
  onSubmit: noop,
  onCancel: noop,
  onBlur: noop,
  submitButtonText: undefined,
};

export default ResponsiveModal;
